import { fetchPatientRecord, fetchPatientVisits, getChatCompletionAssets } from "@common/lib/supabaseClient";
import { PatientRecord, VisitHeader } from "@common/utils/types";

/** Everything the visit page loads for one visit, before any of it goes into the store. */
export type PatientRecordLoad = {
    data: PatientRecord | null;
    error: unknown;
    chatCompletions: PatientRecord["chat_completions"];
    patientVisits: VisitHeader[] | null;
};

/** Fetches a visit, its documents' asset links and the patient's other visits. No store writes. */
export async function loadPatientRecord(patientRecordId: string): Promise<PatientRecordLoad> {
    const { data, error } = await fetchPatientRecord(patientRecordId);
    if (!data) return { data, error, chatCompletions: undefined, patientVisits: null };

    // Extract included assets
    let chatCompletions = data.chat_completions;
    if (chatCompletions && Array.isArray(chatCompletions)) {
        const { data: chatCompletionAssets } = await getChatCompletionAssets(
            chatCompletions.map((completion) => completion.id).filter((id) => id !== null) as string[],
        );

        if (chatCompletionAssets) {
            chatCompletions = chatCompletions.map((completion) => {
                const assets = chatCompletionAssets.filter((asset) => asset.chat_completion_id === completion.id);
                const transcriptSectionIds = assets
                    .map((asset) => asset.transcript_section_id)
                    .filter((id) => id !== null);
                const documentAssetIds = assets.map((asset) => asset.document_asset_id).filter((id) => id !== null);
                return {
                    ...completion,
                    transcript_section_ids: transcriptSectionIds,
                    document_asset_ids: documentAssetIds,
                };
            });
        }
    }

    // Extract other patient visits
    let patientVisits: VisitHeader[] | null = null;
    if (data.patients?.id) {
        const { data: visits } = await fetchPatientVisits(data.patients.id);
        patientVisits = visits ?? null;
    }

    return { data, error, chatCompletions, patientVisits };
}

// EN-18 Review Mode starts the next visit's load when a move is clicked, so it runs alongside the save and the
// stage write instead of after them. A load is only reused while it is this young; older ones are refetched.
const PREFETCH_TTL_MS = 15_000;
const prefetched = new Map<string, { promise: Promise<PatientRecordLoad>; startedAt: number }>();

const isFresh = (entry: { startedAt: number }) => Date.now() - entry.startedAt <= PREFETCH_TTL_MS;

export function prefetchPatientRecord(patientRecordId: string): void {
    prefetched.forEach((entry, id) => {
        if (!isFresh(entry)) prefetched.delete(id);
    });
    if (prefetched.has(patientRecordId)) return;
    const promise = loadPatientRecord(patientRecordId);
    // Nobody may ever take this load; don't let its failure surface as an unhandled rejection.
    promise.catch(() => undefined);
    prefetched.set(patientRecordId, { promise, startedAt: Date.now() });
}

export function clearPrefetchedPatientRecords(): void {
    prefetched.clear();
}

/**
 * Loads a visit, reusing a fresh prefetch of it when there is one. A prefetch is used once, and only when it found
 * the visit: a failure or a not-found is retried with a fresh fetch rather than trusted.
 */
export async function loadPatientRecordPreferPrefetched(patientRecordId: string): Promise<PatientRecordLoad> {
    const entry = prefetched.get(patientRecordId);
    prefetched.delete(patientRecordId);
    if (entry && isFresh(entry)) {
        const result = await entry.promise.catch(() => null);
        if (result?.data) return result;
    }
    return loadPatientRecord(patientRecordId);
}
